/* eslint-disable max-lines */
import { FunnelIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { defaultConfig } from "../../../hooks/useSearch";
import {
  IFilterGroup,
  IFilters
} from "../../../interfaces/components/HeaderPage.types";
import { cx } from "../../../utils/classnames";
import Animation from "../../ui/Animation";
import Box from "../../ui/Box";
import ClickAwayListener from "../../ui/Clickaway";
import Checkbox from "../../ui/input/Checkbox";

const Filter: FC<IFilters> = ({
  options,
  filterName,
  filterSelctor,
  multiple,
  groups
}) => {
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();

  // A dropdown is always a list of sections; the flat props are just the one-section case,
  // which is what every page other than reservations uses.
  const sections: IFilterGroup[] = groups?.length
    ? groups
    : [{ options, filterSelctor, multiple }];

  const selectorOf = (group: IFilterGroup) =>
    group.filterSelctor || defaultConfig.filterSelector;

  // Multi-select keeps every choice in ONE comma-separated param rather than repeating the
  // key, because parseQueryString (used by useSearch) collapses duplicate keys to the last.
  const selectedOf = (group: IFilterGroup) =>
    (searchParams.get(selectorOf(group)) || "").split(",").filter(Boolean);

  const handleChange =
    (group: IFilterGroup) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const checked = event.target.checked;
      const selector = selectorOf(group);

      // Always work on a fresh params instance
      const params = new URLSearchParams(searchParams);

      if (group.multiple) {
        const selected = selectedOf(group);
        const next = checked
          ? [...selected, value]
          : selected.filter((el) => el !== value);

        if (next.length) params.set(selector, next.join(","));
        else params.delete(selector);
      } else if (checked) {
        params.set(selector, value);
      } else {
        params.delete(selector);
      }

      params.delete(defaultConfig.paginationSelector);
      setSearchParams(params, { replace: true, state });
    };

  // Clears every section, so one Clear empties both Statuset and Add-ons.
  const onClear = () => {
    const params = new URLSearchParams(searchParams);
    sections.forEach((group) => params.delete(selectorOf(group)));
    params.delete(defaultConfig.paginationSelector);
    setSearchParams(params, { replace: true, state });
    setOpen(false);
  };

  // Badge counts selections across all sections: one status + two add-ons reads as 3.
  const filterLength = sections.reduce(
    (total, group) => total + selectedOf(group).length,
    0
  );

  const hasOptions = sections.some((group) => !!group.options?.length);

  return (
    <div className="ml-3 md:ml-6">
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <div className="relative">
          <button
            onClick={() => setOpen((prev) => !prev)}
            className={cx([
              "flex justify-center items-center leading-5 rounded-lg whitespace-nowrap group",
              "ring-1 transition-shadow duration-200 hover:ring-gray-500 py-2 px-3",
              open ? "ring-primary" : "ring-blue-stroke"
            ])}
          >
            <FunnelIcon
              className={cx([
                "w-4 h-5 duration-500",
                filterLength
                  ? "text-primary"
                  : open
                  ? "text-black"
                  : "text-neutral group-hover:text-black duration-200"
              ])}
              {...(filterLength && {
                fill: "currentColor",
                fillRule: "evenodd"
              })}
            />
            <span
              className={cx([
                "md:inline hidden md:ml-2 ml-0",
                filterLength || open
                  ? "text-black"
                  : "text-neutral group-hover:text-black duration-200"
              ])}
            >
              {filterName}
            </span>
          </button>
          <Animation type="height" show={open} leaveInDom={hasOptions}>
            <div
              className="absolute mt-2 right-0 z-50"
              style={{ minWidth: "calc(100% + 80px)" }}
            >
              <Box padding="py-1">
                {sections.map((group, groupIndex) => {
                  const selector = selectorOf(group);
                  const selected = selectedOf(group);

                  return (
                    <div
                      key={`${selector}_${groupIndex}`}
                      className={cx([
                        groupIndex > 0 && "border-t border-gray-200 mt-1 pt-1"
                      ])}
                    >
                      {group.title && (
                        <p
                          className="px-3 pt-1.5 pb-1 text-xs font-semibold uppercase
                          tracking-wide text-neutral-300 select-none"
                        >
                          {group.title}
                        </p>
                      )}
                      {group.options?.map((el) => {
                        const value = el.id.toString();
                        // Unique per section, so two sections can share an option id
                        const uniqueId = `${selector}-${value}`;
                        const checked = group.multiple
                          ? selected.includes(value)
                          : searchParams.get(selector) === value || false;

                        return (
                          <Checkbox
                            key={`${el.label}_${uniqueId}`}
                            label={el.label}
                            name={`${selector}-${el.label.toLowerCase()}`}
                            id={uniqueId}
                            value={value}
                            onChange={handleChange(group)}
                            checked={checked}
                            inputClassName="px-3 py-2 hover:bg-gray-50"
                          />
                        );
                      })}
                    </div>
                  );
                })}
                <div
                  className="border-t border-gray-200 px-3 py-2 flex justify-start items-center text-sm
              hover:bg-gray-50 cursor-pointer group"
                  onClick={onClear}
                >
                  <XMarkIcon
                    className="w-[18px] h-[18px] mr-2 text-neutral-300
                    group-hover:text-neutral duration-300
                  "
                    strokeWidth={2}
                  />
                  Clear
                </div>
              </Box>
            </div>
          </Animation>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default Filter;
